import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Settings2, 
  User, 
  Bell, 
  Palette, 
  Shield, 
  CreditCard, 
  Globe, 
  Download, 
  Mail,
  Smartphone,
  Lock,
  Eye,
  Check,
  X,
  Search,
  Filter,
  ChevronRight,
  Zap,
  Database,
  Activity,
  Sun,
  Moon,
  Monitor,
  Calendar,
  DollarSign,
  Settings,
  TestTube,
  Tags
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import AccountSettings from "@/components/settings/account-settings";
import { useLocation } from "wouter";

// Define settings structure with real-time sync capabilities
interface SettingsSection {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  badge?: string;
  color: string;
}

interface UserPreferences {
  theme: 'light' | 'dark' | 'system';
  language: string;
  timezone: string;
  dateFormat: string;
  currency: string;
  notifications: {
    email: boolean;
    push: boolean;
    sms: boolean;
    renewals: boolean;
    costAlerts: boolean;
    systemUpdates: boolean;
  };
  privacy: {
    profileVisibility: 'public' | 'private' | 'team';
    activityTracking: boolean;
    dataCollection: boolean;
  };
  security: {
    twoFactorEnabled: boolean;
    sessionTimeout: number;
    allowedDevices: number;
  };
}

const settingsSections: SettingsSection[] = [
  {
    id: 'profile',
    title: 'Profile',
    description: 'Manage your personal information and profile details',
    icon: <User className="h-5 w-5" />,
    color: 'bg-blue-500'
  },
  {
    id: 'account',
    title: 'Account',
    description: 'Account management, reports, and data export',
    icon: <Settings2 className="h-5 w-5" />,
    color: 'bg-slate-500'
  },
  {
    id: 'notifications',
    title: 'Notifications',
    description: 'Control how and when you receive notifications',
    icon: <Bell className="h-5 w-5" />,
    color: 'bg-orange-500'
  },
  {
    id: 'currency',
    title: 'Currency',
    description: 'Set your preferred currency for displaying costs',
    icon: <DollarSign className="h-5 w-5" />,
    color: 'bg-green-500'
  },
  {
    id: 'categories',
    title: 'Categories',
    description: 'Manage application categories for better organization',
    icon: <Tags className="h-5 w-5" />,
    color: 'bg-purple-500'
  }
];

export default function ModernSettings() {
  const [activeSection, setActiveSection] = useState('profile');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [preferences, setPreferences] = useState<UserPreferences>({
    theme: 'light',
    language: 'en',
    timezone: 'UTC',
    dateFormat: 'MM/DD/YYYY',
    currency: 'USD',
    notifications: {
      email: true,
      push: true,
      sms: false,
      renewals: true,
      costAlerts: true,
      systemUpdates: false
    },
    privacy: {
      profileVisibility: 'team',
      activityTracking: true,
      dataCollection: true
    },
    security: {
      twoFactorEnabled: false,
      sessionTimeout: 30,
      allowedDevices: 5
    }
  });

  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();

  // Real-time settings synchronization
  const { data: userSettings, isLoading: settingsLoading } = useQuery({
    queryKey: ['/api/user/settings'],
    staleTime: 0,
    refetchInterval: 10000, // Refresh every 10 seconds
    refetchOnWindowFocus: true
  });

  // Apply the saved settings once loaded -- without this, every toggle keeps
  // reverting to the hardcoded initial state on refresh since userSettings
  // was being fetched but never actually applied to the local state.
  useEffect(() => {
    if (userSettings) {
      setPreferences((prev) => ({ ...prev, ...(userSettings as Partial<UserPreferences>) }));
    }
  }, [userSettings]);

  // Mutations for real-time updates
  const updateSettingsMutation = useMutation({
    mutationFn: async (newSettings: Partial<UserPreferences>) => {
      return apiRequest('/api/user/settings', {
        method: 'PUT',
        body: newSettings
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/user/settings'] });
      toast({
        title: "Settings Updated",
        description: "Your preferences have been saved successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Update Failed",
        description: "Failed to save your settings. Please try again.",
        variant: "destructive"
      });
    }
  });

  // Non-admins only get the Profile tab — Account/Notifications/Currency/Categories
  // manage tenant-wide settings and stay admin-only.
  const isAdmin = user?.role === 'admin';
  const visibleSections = isAdmin
    ? settingsSections
    : settingsSections.filter(section => section.id === 'profile');

  // Filtered sections based on search
  const filteredSections = visibleSections.filter(section =>
    section.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    section.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSettingChange = (category: keyof UserPreferences, key: string, value: any) => {
    const currentCategory = preferences[category];
    if (typeof currentCategory === 'object' && currentCategory !== null) {
      const newPreferences = {
        ...preferences,
        [category]: {
          ...currentCategory,
          [key]: value
        }
      };
      setPreferences(newPreferences);
      
      // Real-time sync
      updateSettingsMutation.mutate({ [category]: newPreferences[category] });
    }
  };

  const renderSectionContent = () => {
    switch (activeSection) {
      case 'profile':
        return <ProfileSettings user={user} preferences={preferences} onChange={handleSettingChange} />;
      case 'account':
        return <AccountSettings user={user} preferences={preferences} onChange={handleSettingChange} />;
      case 'notifications':
        return <NotificationSettings preferences={preferences} onChange={handleSettingChange} />;
      case 'currency':
        return <CurrencySettings />;
      default:
        return <ProfileSettings user={user} preferences={preferences} onChange={handleSettingChange} />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Modern Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-900 to-slate-600 dark:from-slate-100 dark:to-slate-400 bg-clip-text text-transparent">
              Settings
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mt-2">
              Manage your account preferences and application settings
            </p>
          </div>
        </motion.div>

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 min-h-[600px] lg:min-h-[800px]">
          {/* Mobile Settings Toggle */}
          <div className="lg:hidden">
            <Button
              variant="outline"
              onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
              className="w-full mb-4 flex items-center gap-2"
            >
              <Settings className="h-4 w-4" />
              {activeSection.charAt(0).toUpperCase() + activeSection.slice(1)} Settings
            </Button>
          </div>

          {/* Sidebar Navigation - Responsive */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className={`lg:w-80 lg:flex-shrink-0 ${isMobileSidebarOpen ? 'block' : 'hidden lg:block'}`}
          >
            <Card className="lg:sticky lg:top-4 shadow-lg border-0 bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl">
              <CardHeader className="pb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search settings..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 border-0 bg-slate-100 dark:bg-slate-700"
                  />
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-auto max-h-[600px] lg:h-[700px]">
                  <div className="space-y-2 p-4 pt-0">
                    {filteredSections.map((section) => (
                      <motion.button
                        key={section.id}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => {
                          // Navigate to dedicated category management page if it's categories section
                          if (section.id === 'categories') {
                            navigate('/settings/categories');
                            return;
                          }
                          
                          setActiveSection(section.id);
                          // Close mobile sidebar after selection
                          if (window.innerWidth < 1024) {
                            setIsMobileSidebarOpen(false);
                          }
                        }}
                        className={`w-full flex items-center gap-3 p-3 lg:p-4 rounded-xl text-left transition-all duration-200 ${
                          activeSection === section.id
                            ? 'bg-primary text-primary-foreground shadow-lg'
                            : 'hover:bg-slate-100 dark:hover:bg-slate-700'
                        }`}
                      >
                        <div className={`p-2 rounded-lg ${section.color} ${
                          activeSection === section.id ? 'bg-white/20' : 'bg-opacity-10'
                        }`}>
                          {section.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-sm">{section.title}</span>
                            {section.badge && (
                              <Badge variant="secondary" className="text-xs">
                                {section.badge}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs opacity-70 leading-tight hidden sm:block">{section.description}</p>
                        </div>
                        <ChevronRight className="h-4 w-4 opacity-50 flex-shrink-0" />
                      </motion.button>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </motion.div>

          {/* Main Content - Responsive Width */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex-1 min-w-0 w-full"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={activeSection}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2 }}
              >
                {renderSectionContent()}
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

// Profile Settings Component
function ProfileSettings({ user, preferences, onChange }: any) {
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(user?.fullName || '');
  const [isEditingUsername, setIsEditingUsername] = useState(false);
  const [editedUsername, setEditedUsername] = useState(user?.username || '');
  const [isEditingOrg, setIsEditingOrg] = useState(false);
  const [editedOrgName, setEditedOrgName] = useState(user?.tenantName || '');
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [editedEmail, setEditedEmail] = useState(user?.email || '');
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isAdmin = user?.role === 'admin';

  const { data: emailRequestData } = useQuery({
    queryKey: ['/api/user/email-change-request'],
    queryFn: () => apiRequest('/api/user/email-change-request'),
    enabled: !isAdmin,
  });
  const pendingEmailRequest = emailRequestData?.request;

  const updateOrgMutation = useMutation({
    mutationFn: async (name: string) => {
      return await apiRequest('/api/tenant/name', {
        method: 'PATCH',
        body: { name }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      setIsEditingOrg(false);
      toast({ title: "Organization Updated", description: "Your organization name has been updated." });
    },
    onError: (error: any) => {
      toast({
        title: "Update Failed",
        description: error.message || "There was an error updating the organization name.",
        variant: "destructive"
      });
    }
  });

  const requestEmailChangeMutation = useMutation({
    mutationFn: async (email: string) => {
      return await apiRequest('/api/user/request-email-change', {
        method: 'POST',
        body: { newEmail: email }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/user/email-change-request'] });
      setIsEmailDialogOpen(false);
      setNewEmail('');
      toast({
        title: "Request Submitted",
        description: "Your admin has been notified and will need to approve this change."
      });
    },
    onError: (error: any) => {
      toast({
        title: "Request Failed",
        description: error.message || "There was an error submitting your request.",
        variant: "destructive"
      });
    }
  });

  // Profile update mutation with proper cache invalidation
  const updateProfileMutation = useMutation({
    mutationFn: async (updates: { fullName?: string, username?: string, email?: string }) => {
      return await apiRequest('/api/user/update-profile', {
        method: 'PATCH',
        body: updates
      });
    },
    onSuccess: () => {
      // Invalidate and refetch user settings to update the UI immediately
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      queryClient.invalidateQueries({ queryKey: ['/api/user/settings'] });
      setIsEditingName(false);
      setIsEditingUsername(false);
      setIsEditingEmail(false);
      toast({
        title: "Profile Updated",
        description: "Your profile has been updated successfully."
      });
    },
    onError: (error: any) => {
      toast({
        title: "Update Failed",
        description: error.message || "There was an error updating your profile. Please try again.",
        variant: "destructive"
      });
    }
  });

  const handleNameSave = () => {
    updateProfileMutation.mutate({ fullName: editedName });
  };

  const handleUsernameSave = () => {
    updateProfileMutation.mutate({ username: editedUsername });
  };

  // Generate avatar background color based on first letter
  const getAvatarColor = (name: string) => {
    const firstLetter = (name?.charAt(0) || 'U').toUpperCase();
    const colors = [
      'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-pink-500', 
      'bg-indigo-500', 'bg-orange-500', 'bg-teal-500', 'bg-red-500'
    ];
    return colors[firstLetter.charCodeAt(0) % colors.length];
  };

  return (
    <div className="space-y-6">
      <Card className="shadow-lg border-0 bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Profile Information
          </CardTitle>
          <CardDescription>
            View and manage your account details. Your name and username can be edited.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-6">
            <div className={`h-20 w-20 rounded-full flex items-center justify-center text-white text-2xl font-semibold ${getAvatarColor(user?.fullName || user?.username || '')}`}>
              {(user?.fullName?.charAt(0) || user?.username?.charAt(0) || 'U').toUpperCase()}
            </div>
            <div className="flex-1">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Profile picture shows the first letter of your name with a unique color.
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="fullName">Full Name</Label>
              {isEditingName ? (
                <div className="flex gap-2">
                  <Input
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    className="border-0 bg-slate-100 dark:bg-slate-700"
                    autoFocus
                  />
                  <Button 
                    size="sm" 
                    onClick={handleNameSave}
                    disabled={updateProfileMutation.isPending}
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => {
                      setIsEditingName(false);
                      setEditedName(user?.fullName || '');
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Input
                    id="fullName"
                    value={user?.fullName || ''}
                    readOnly
                    className="border-0 bg-slate-100 dark:bg-slate-700 cursor-default"
                  />
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setIsEditingName(true)}
                  >
                    Edit
                  </Button>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              {isEditingUsername ? (
                <div className="flex gap-2">
                  <Input
                    value={editedUsername}
                    onChange={(e) => setEditedUsername(e.target.value)}
                    className="border-0 bg-slate-100 dark:bg-slate-700"
                    autoFocus
                  />
                  <Button 
                    size="sm" 
                    onClick={handleUsernameSave}
                    disabled={updateProfileMutation.isPending}
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => {
                      setIsEditingUsername(false);
                      setEditedUsername(user?.username || '');
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Input
                    id="username"
                    value={user?.username || ''}
                    placeholder="Set a username"
                    readOnly
                    className="border-0 bg-slate-100 dark:bg-slate-700 cursor-default"
                  />
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setIsEditingUsername(true)}
                  >
                    Edit
                  </Button>
                </div>
              )}
              <p className="text-xs text-slate-500">Pick a unique name to identify yourself</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              {isAdmin ? (
                isEditingEmail ? (
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      value={editedEmail}
                      onChange={(e) => setEditedEmail(e.target.value)}
                      className="border-0 bg-slate-100 dark:bg-slate-700"
                      autoFocus
                    />
                    <Button
                      size="sm"
                      onClick={() => updateProfileMutation.mutate({ email: editedEmail })}
                      disabled={updateProfileMutation.isPending}
                    >
                      <Check className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setIsEditingEmail(false);
                        setEditedEmail(user?.email || '');
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Input
                      id="email"
                      type="email"
                      value={user?.email || ''}
                      readOnly
                      className="border-0 bg-slate-100 dark:bg-slate-700 cursor-default"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setIsEditingEmail(true)}
                    >
                      Edit
                    </Button>
                  </div>
                )
              ) : (
                <div className="flex items-center gap-2">
                  <Input
                    id="email"
                    type="email"
                    value={user?.email || ''}
                    readOnly
                    className="border-0 bg-slate-100 dark:bg-slate-700 cursor-default opacity-60"
                  />
                  {!pendingEmailRequest && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setIsEmailDialogOpen(true)}
                    >
                      Request Change
                    </Button>
                  )}
                </div>
              )}
              {isAdmin ? (
                <p className="text-xs text-slate-500">As an admin, you can change your email directly</p>
              ) : pendingEmailRequest ? (
                <p className="text-xs text-amber-600">
                  Pending approval: change to {pendingEmailRequest.requestedEmail}
                </p>
              ) : (
                <p className="text-xs text-slate-500">Email changes require admin approval</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Input
                id="role"
                value={user?.role || ''}
                readOnly
                className="border-0 bg-slate-100 dark:bg-slate-700 cursor-default opacity-60"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="organization">Organization</Label>
              {isEditingOrg ? (
                <div className="flex gap-2">
                  <Input
                    value={editedOrgName}
                    onChange={(e) => setEditedOrgName(e.target.value)}
                    className="border-0 bg-slate-100 dark:bg-slate-700"
                    autoFocus
                  />
                  <Button
                    size="sm"
                    onClick={() => updateOrgMutation.mutate(editedOrgName)}
                    disabled={updateOrgMutation.isPending}
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setIsEditingOrg(false);
                      setEditedOrgName(user?.tenantName || '');
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Input
                    id="organization"
                    value={user?.tenantName || ''}
                    readOnly
                    className="border-0 bg-slate-100 dark:bg-slate-700 cursor-default"
                  />
                  {isAdmin && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setIsEditingOrg(true)}
                    >
                      Edit
                    </Button>
                  )}
                </div>
              )}
              {!isAdmin && (
                <p className="text-xs text-slate-500">Only an organization admin can rename your organization</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isEmailDialogOpen} onOpenChange={setIsEmailDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Email Change</DialogTitle>
            <DialogDescription>
              Your admin will need to review and approve this request before your email is updated.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="new-email">New Email Address</Label>
            <Input
              id="new-email"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="you@company.com"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEmailDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={() => requestEmailChangeMutation.mutate(newEmail)}
              disabled={!newEmail.trim() || requestEmailChangeMutation.isPending}
            >
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Appearance Settings Component
function AppearanceSettings({ preferences, onChange }: any) {
  const themes = [
    { id: 'light', name: 'Light', icon: <Sun className="h-4 w-4" /> },
    { id: 'dark', name: 'Dark', icon: <Moon className="h-4 w-4" /> },
    { id: 'system', name: 'System', icon: <Monitor className="h-4 w-4" /> }
  ];

  return (
    <div className="space-y-6">
      <Card className="shadow-lg border-0 bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            Theme & Display
          </CardTitle>
          <CardDescription>
            Customize your interface appearance and display preferences
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3">
            <Label>Theme Preference</Label>
            <div className="grid grid-cols-3 gap-3">
              {themes.map((theme) => (
                <motion.button
                  key={theme.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => onChange('theme', 'theme', theme.id)}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    preferences.theme === theme.id
                      ? 'border-primary bg-primary/10'
                      : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    {theme.icon}
                    <span className="text-sm font-medium">{theme.name}</span>
                  </div>
                </motion.button>
              ))}
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>Language</Label>
              <Select
                value={preferences.language}
                onValueChange={(value) => onChange('language', 'language', value)}
              >
                <SelectTrigger className="border-0 bg-slate-100 dark:bg-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="es">Spanish</SelectItem>
                  <SelectItem value="fr">French</SelectItem>
                  <SelectItem value="de">German</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Date Format</Label>
              <Select
                value={preferences.dateFormat}
                onValueChange={(value) => onChange('dateFormat', 'dateFormat', value)}
              >
                <SelectTrigger className="border-0 bg-slate-100 dark:bg-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MM/DD/YYYY">MM/DD/YYYY</SelectItem>
                  <SelectItem value="DD/MM/YYYY">DD/MM/YYYY</SelectItem>
                  <SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Notification Settings Component
function NotificationSettings({ preferences, onChange }: any) {
  const { toast } = useToast();
  const [isTestingEmail, setIsTestingEmail] = useState(false);

  const notificationTypes = [
    { 
      key: 'email', 
      label: 'Email Notifications', 
      description: 'Receive all notifications via email through Mailgun',
      icon: Mail,
      color: 'text-blue-500'
    },
    { 
      key: 'renewals', 
      label: 'License Renewals', 
      description: 'Email alerts for upcoming license renewals and expirations',
      icon: Calendar,
      color: 'text-green-500'
    },
    { 
      key: 'costAlerts', 
      label: 'Cost Alerts', 
      description: 'Email notifications for budget thresholds and spending alerts',
      icon: DollarSign,
      color: 'text-orange-500'
    },
    { 
      key: 'systemUpdates', 
      label: 'System Updates', 
      description: 'Email notifications for platform updates and maintenance windows',
      icon: Settings,
      color: 'text-purple-500'
    }
  ];

  const testEmailNotification = async () => {
    setIsTestingEmail(true);
    try {
      // Get current user data to send email to their address
      const userResponse = await fetch('/api/user/settings');
      const userData = await userResponse.json();
      
      const response = await fetch('/api/test-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: userData.user?.email || 'test@example.com',
          subject: 'SAASGAMA Notification Test',
          message: 'This is a test notification from your SAASGAMA notification system. All systems are working correctly!'
        })
      });

      if (response.ok) {
        toast({
          title: "Test email sent",
          description: "Check your email inbox for the test notification.",
        });
      } else {
        const error = await response.text();
        toast({
          title: "Test email failed",
          description: error || "Failed to send test email. Please check your email configuration.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Test email error:', error);
      toast({
        title: "Test email failed",
        description: "An error occurred while sending the test email.",
        variant: "destructive",
      });
    } finally {
      setIsTestingEmail(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="shadow-lg border-0 bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Email Notification Preferences
          </CardTitle>
          <CardDescription>
            Control when you receive email notifications via Mailgun
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {notificationTypes.map((type) => {
            const IconComponent = type.icon;
            return (
              <div key={type.key} className="flex items-center justify-between p-4 rounded-lg bg-slate-50 dark:bg-slate-800">
                <div className="flex items-start gap-3">
                  <IconComponent className={`h-5 w-5 ${type.color} mt-0.5`} />
                  <div className="flex-1">
                    <div className="font-medium">{type.label}</div>
                    <div className="text-sm text-slate-500">{type.description}</div>
                  </div>
                </div>
                <Switch
                  checked={preferences.notifications[type.key]}
                  onCheckedChange={(checked) => onChange('notifications', type.key, checked)}
                />
              </div>
            );
          })}
          
          <Separator />
          
          {/* Test Email Section */}
          <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <TestTube className="h-5 w-5 text-blue-500" />
                <div>
                  <div className="font-medium">Test Email Notifications</div>
                  <div className="text-sm text-slate-500">
                    Send a test email to verify your notification settings
                  </div>
                </div>
              </div>
              <Button 
                onClick={testEmailNotification}
                disabled={isTestingEmail || !preferences.notifications.email}
                size="sm"
                variant="outline"
              >
                {isTestingEmail ? 'Sending...' : 'Send Test'}
              </Button>
            </div>
            {!preferences.notifications.email && (
              <p className="text-xs text-slate-500 mt-2">
                Enable email notifications above to test the system
              </p>
            )}
          </div>

          {/* Email Provider Info */}
          <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800">
            <h4 className="font-medium mb-2 flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Email Provider Information
            </h4>
            <ul className="text-sm text-slate-600 space-y-1">
              <li>• All notifications are sent via Mailgun email service</li>
              <li>• Emails are sent from your configured SAASGAMA address</li>
              <li>• You can test the email system anytime with the button above</li>
              <li>• Disable specific notification types if you prefer less emails</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Privacy Settings Component
function PrivacySettings({ preferences, onChange }: any) {
  return (
    <div className="space-y-6">
      <Card className="shadow-lg border-0 bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Privacy & Data Control
          </CardTitle>
          <CardDescription>
            Manage your privacy settings and data collection preferences
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-3">
              <Label>Profile Visibility</Label>
              <Select
                value={preferences.privacy.profileVisibility}
                onValueChange={(value) => onChange('privacy', 'profileVisibility', value)}
              >
                <SelectTrigger className="border-0 bg-slate-100 dark:bg-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">Public</SelectItem>
                  <SelectItem value="team">Team Only</SelectItem>
                  <SelectItem value="private">Private</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50 dark:bg-slate-800">
              <div>
                <div className="font-medium">Activity Tracking</div>
                <div className="text-sm text-slate-500">Track your usage for analytics</div>
              </div>
              <Switch
                checked={preferences.privacy.activityTracking}
                onCheckedChange={(checked) => onChange('privacy', 'activityTracking', checked)}
              />
            </div>

            <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50 dark:bg-slate-800">
              <div>
                <div className="font-medium">Data Collection</div>
                <div className="text-sm text-slate-500">Allow data collection for improvements</div>
              </div>
              <Switch
                checked={preferences.privacy.dataCollection}
                onCheckedChange={(checked) => onChange('privacy', 'dataCollection', checked)}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Security Settings Component
function SecuritySettings({ preferences, onChange }: any) {
  return (
    <div className="space-y-6">
      <Card className="shadow-lg border-0 bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            Security Settings
          </CardTitle>
          <CardDescription>
            Manage your account security and authentication
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50 dark:bg-slate-800">
            <div>
              <div className="font-medium">Two-Factor Authentication</div>
              <div className="text-sm text-slate-500">Add an extra layer of security</div>
            </div>
            <Switch
              checked={preferences.security.twoFactorEnabled}
              onCheckedChange={(checked) => onChange('security', 'twoFactorEnabled', checked)}
            />
          </div>

          <div className="space-y-3">
            <Label>Session Timeout (minutes)</Label>
            <div className="px-4">
              <Slider
                value={[preferences.security.sessionTimeout]}
                onValueChange={(value) => onChange('security', 'sessionTimeout', value[0])}
                max={120}
                min={15}
                step={15}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-slate-500 mt-1">
                <span>15 min</span>
                <span>{preferences.security.sessionTimeout} min</span>
                <span>120 min</span>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <Label>Maximum Allowed Devices</Label>
            <div className="px-4">
              <Slider
                value={[preferences.security.allowedDevices]}
                onValueChange={(value) => onChange('security', 'allowedDevices', value[0])}
                max={10}
                min={1}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-slate-500 mt-1">
                <span>1 device</span>
                <span>{preferences.security.allowedDevices} devices</span>
                <span>10 devices</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Billing Settings Placeholder
function BillingSettings() {
  return (
    <Card className="shadow-lg border-0 bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Billing & Subscription
        </CardTitle>
        <CardDescription>
          Manage your subscription and payment methods
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="text-center py-8">
          <CreditCard className="h-12 w-12 mx-auto text-slate-400 mb-4" />
          <h3 className="text-lg font-medium mb-2">Billing Integration</h3>
          <p className="text-slate-500">
            Billing settings will be integrated with the existing subscription system
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

// Currency Settings Component
function CurrencySettings() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const CURRENCIES = [
    { code: "USD", name: "US Dollar", symbol: "$" },
    { code: "EUR", name: "Euro", symbol: "€" },
    { code: "GBP", name: "British Pound", symbol: "£" },
    { code: "INR", name: "Indian Rupee", symbol: "₹" },
    { code: "CAD", name: "Canadian Dollar", symbol: "C$" },
    { code: "AUD", name: "Australian Dollar", symbol: "A$" },
    { code: "JPY", name: "Japanese Yen", symbol: "¥" },
    { code: "SGD", name: "Singapore Dollar", symbol: "S$" },
    { code: "AED", name: "UAE Dirham", symbol: "د.إ" },
    { code: "MYR", name: "Malaysian Ringgit", symbol: "RM" },
  ];

  const { data, isLoading } = useQuery<{ baseCurrency: string }>({
    queryKey: ["/api/currency-settings"],
    queryFn: async () => {
      const res = await fetch("/api/currency-settings");
      if (!res.ok) return { baseCurrency: "USD" };
      return res.json();
    },
  });

  const [selected, setSelected] = useState("USD");

  // Sync with server data once loaded
  useEffect(() => {
    if (data?.baseCurrency) setSelected(data.baseCurrency);
  }, [data?.baseCurrency]);

  const saveMutation = useMutation({
    mutationFn: async (code: string) => {
      return await apiRequest("POST", "/api/currency-settings", { baseCurrency: code });
    },
    onSuccess: (_, code) => {
      queryClient.invalidateQueries({ queryKey: ["/api/currency-settings"] });
      // Update the module-level formatter immediately
      import("@/lib/utils").then(({ setActiveCurrency }) => setActiveCurrency(code));
      toast({ title: "Currency updated", description: `All costs will now display in ${code}.` });
    },
    onError: () => {
      toast({ title: "Failed to save", variant: "destructive" });
    },
  });

  const current = CURRENCIES.find(c => c.code === selected) ?? CURRENCIES[0];

  return (
    <div className="space-y-6">
      <Card className="shadow-lg border-0 bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Display Currency
          </CardTitle>
          <CardDescription>
            All costs across the platform — dashboard, licenses, reports — will display in this currency.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label>Select Currency</Label>
            <Select value={selected} onValueChange={setSelected} disabled={isLoading}>
              <SelectTrigger className="border-0 bg-slate-100 dark:bg-slate-700 w-full max-w-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CURRENCIES.map(c => (
                  <SelectItem key={c.code} value={c.code}>
                    <span className="font-medium mr-2">{c.symbol}</span>
                    {c.code} — {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-lg bg-slate-50 dark:bg-slate-800 p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-lg font-bold text-green-700">
              {current.symbol}
            </div>
            <div>
              <p className="font-semibold">{current.name}</p>
              <p className="text-sm text-slate-500">Example: {current.symbol}1,250</p>
            </div>
          </div>

          <Button
            onClick={() => saveMutation.mutate(selected)}
            disabled={saveMutation.isPending || selected === data?.baseCurrency}
            className="w-full max-w-xs"
          >
            {saveMutation.isPending ? "Saving..." : "Save Currency Preference"}
          </Button>
        </CardContent>
      </Card>

      <Card className="shadow-lg border-0 bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl">
        <CardHeader>
          <CardTitle className="text-base">Supported Currencies</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {CURRENCIES.map(c => (
              <button
                key={c.code}
                onClick={() => setSelected(c.code)}
                className={`flex items-center gap-2 p-3 rounded-lg border-2 text-left transition-all ${
                  selected === c.code
                    ? "border-primary bg-primary/5"
                    : "border-slate-200 dark:border-slate-700 hover:border-slate-300"
                }`}
              >
                <span className="text-lg font-bold w-6 text-center">{c.symbol}</span>
                <div className="min-w-0">
                  <p className="font-medium text-sm">{c.code}</p>
                  <p className="text-xs text-slate-500 truncate">{c.name}</p>
                </div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Integrations Settings Placeholder
function IntegrationsSettings() {
  return (
    <Card className="shadow-lg border-0 bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Zap className="h-5 w-5" />
          Integrations & APIs
        </CardTitle>
        <CardDescription>
          Connect with external services and manage API access
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="text-center py-8">
          <Zap className="h-12 w-12 mx-auto text-slate-400 mb-4" />
          <h3 className="text-lg font-medium mb-2">Integration Hub</h3>
          <p className="text-slate-500">
            Integration management will be available in the next update
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

// Data Management Settings
function DataManagementSettings() {
  const { toast } = useToast();
  const [isExporting, setIsExporting] = useState(false);

  // Fetch applications and licenses for export
  const { data: applications = [] } = useQuery({
    queryKey: ['/api/applications'],
  });

  const { data: licenses = [] } = useQuery({
    queryKey: ['/api/licenses'],
  });

  const { data: users = [] } = useQuery({
    queryKey: ['/api/users'],
  });

  const exportAllData = async () => {
    setIsExporting(true);
    try {
      // Prepare comprehensive export data
      const exportData = [];
      
      // Add applications
      applications.forEach((app: any) => {
        exportData.push({
          type: "application",
          id: app.id,
          name: app.name,
          vendor: app.vendor,
          category: app.category,
          status: app.status,
          website: app.website || '',
          description: app.description || '',
          addedAt: app.addedAt ? new Date(app.addedAt).toISOString().split('T')[0] : ''
        });
      });

      // Add licenses with application details
      licenses.forEach((license: any) => {
        const app = applications.find((a: any) => a.id === license.applicationId);
        exportData.push({
          type: "license",
          id: license.id,
          name: license.name,
          applicationName: app?.name || 'Unknown',
          applicationId: license.applicationId,
          totalSeats: license.totalSeats,
          assignedSeats: license.assignedSeats,
          cost: license.cost,
          currency: license.currency,
          billingCycle: license.billingCycle,
          startDate: license.startDate ? new Date(license.startDate).toISOString().split('T')[0] : '',
          renewalDate: license.renewalDate ? new Date(license.renewalDate).toISOString().split('T')[0] : '',
          status: license.status,
          description: license.description || ''
        });
      });

      // Add users (excluding sensitive data)
      users.forEach((user: any) => {
        exportData.push({
          type: "user",
          id: user.id,
          username: user.username,
          fullName: user.fullName || '',
          email: user.email,
          role: user.role,
          department: user.department || '',
          addedAt: user.createdAt ? new Date(user.createdAt).toISOString().split('T')[0] : ''
        });
      });

      if (exportData.length === 0) {
        toast({
          title: "No data to export",
          description: "There is no data available for export.",
          variant: "destructive",
        });
        return;
      }

      // Use the existing exportToCsv utility
      const { exportToCsv } = await import("@/lib/utils");
      exportToCsv(exportData, 'saasgama_complete_export', Object.keys(exportData[0]));
      
      toast({
        title: "Export successful",
        description: `Successfully exported ${exportData.length} records including applications, licenses, and users.`,
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Export failed",
        description: "There was an error exporting your data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const exportLicensesOnly = async () => {
    setIsExporting(true);
    try {
      if (licenses.length === 0) {
        toast({
          title: "No licenses to export",
          description: "There are no licenses to export.",
          variant: "destructive",
        });
        return;
      }

      // Create enhanced licenses with application names
      const enhancedLicenses = licenses.map((license: any) => {
        const application = applications.find((app: any) => app.id === license.applicationId);
        return {
          ...license,
          applicationName: application?.name || 'Unknown Application',
          startDate: license.startDate ? new Date(license.startDate).toISOString().split('T')[0] : '',
          renewalDate: license.renewalDate ? new Date(license.renewalDate).toISOString().split('T')[0] : ''
        };
      });

      const headers = [
        'name', 'applicationName', 'applicationId', 'totalSeats', 'assignedSeats',
        'cost', 'currency', 'billingCycle', 'startDate', 'renewalDate', 'status', 'description'
      ];

      const { exportToCsv } = await import("@/lib/utils");
      exportToCsv(enhancedLicenses, 'saasgama_licenses_export', headers);
      
      toast({
        title: "Export successful",
        description: `Successfully exported ${licenses.length} licenses.`,
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Export failed",
        description: "There was an error exporting licenses. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const exportApplicationsOnly = async () => {
    setIsExporting(true);
    try {
      if (applications.length === 0) {
        toast({
          title: "No applications to export",
          description: "There are no applications to export.",
          variant: "destructive",
        });
        return;
      }

      const formattedApplications = applications.map((app: any) => ({
        ...app,
        addedAt: app.addedAt ? new Date(app.addedAt).toISOString().split('T')[0] : ''
      }));

      const headers = ['name', 'vendor', 'category', 'status', 'website', 'description', 'addedAt'];

      const { exportToCsv } = await import("@/lib/utils");
      exportToCsv(formattedApplications, 'saasgama_applications_export', headers);
      
      toast({
        title: "Export successful",
        description: `Successfully exported ${applications.length} applications.`,
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Export failed",
        description: "There was an error exporting applications. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Card className="shadow-lg border-0 bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="h-5 w-5" />
          Data Management
        </CardTitle>
        <CardDescription>
          Export and manage your SAASGAMA data
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Complete Data Export */}
          <Card className="p-4">
            <div className="flex items-start gap-3 mb-3">
              <Download className="h-5 w-5 text-blue-500 mt-1" />
              <div className="flex-1">
                <h3 className="font-medium mb-1">Complete Export</h3>
                <p className="text-sm text-slate-500">
                  Export all applications, licenses, and users in one file
                </p>
              </div>
            </div>
            <Button 
              onClick={exportAllData} 
              disabled={isExporting}
              className="w-full"
              size="sm"
            >
              {isExporting ? 'Exporting...' : 'Export All Data'}
            </Button>
          </Card>

          {/* Licenses Export */}
          <Card className="p-4">
            <div className="flex items-start gap-3 mb-3">
              <Database className="h-5 w-5 text-green-500 mt-1" />
              <div className="flex-1">
                <h3 className="font-medium mb-1">Licenses Only</h3>
                <p className="text-sm text-slate-500">
                  Export all licenses with application details
                </p>
              </div>
            </div>
            <Button 
              onClick={exportLicensesOnly} 
              disabled={isExporting}
              variant="outline"
              className="w-full"
              size="sm"
            >
              {isExporting ? 'Exporting...' : 'Export Licenses'}
            </Button>
          </Card>

          {/* Applications Export */}
          <Card className="p-4">
            <div className="flex items-start gap-3 mb-3">
              <Activity className="h-5 w-5 text-purple-500 mt-1" />
              <div className="flex-1">
                <h3 className="font-medium mb-1">Applications Only</h3>
                <p className="text-sm text-slate-500">
                  Export application catalog and details
                </p>
              </div>
            </div>
            <Button 
              onClick={exportApplicationsOnly} 
              disabled={isExporting}
              variant="outline"
              className="w-full"
              size="sm"
            >
              {isExporting ? 'Exporting...' : 'Export Applications'}
            </Button>
          </Card>

          {/* Data Summary */}
          <Card className="p-4">
            <div className="flex items-start gap-3 mb-3">
              <Zap className="h-5 w-5 text-orange-500 mt-1" />
              <div className="flex-1">
                <h3 className="font-medium mb-1">Data Summary</h3>
                <div className="text-sm text-slate-600 space-y-1">
                  <p>Applications: {applications.length}</p>
                  <p>Licenses: {licenses.length}</p>
                  <p>Users: {users.length}</p>
                </div>
              </div>
            </div>
          </Card>
        </div>

        <Separator />

        <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
          <h4 className="font-medium mb-2 flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Export Information
          </h4>
          <ul className="text-sm text-slate-600 space-y-1">
            <li>• All exports are in CSV format for easy import into other systems</li>
            <li>• User data excludes passwords and other sensitive information</li>
            <li>• Complete export includes all data types in a single file</li>
            <li>• Individual exports focus on specific data categories</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
